import "./Header";

import React, { PropsWithChildren, useEffect, useMemo, useState } from "react";
import Routes, { Tab } from "@greeter-guest/utility/Routes";
import { GreeterEventPageRouteHandler } from "@greeter-guest/pages/GreeterEventPage";
import {
  IonApp,
  IonTabs,
  IonTabBar,
  useIonViewWillEnter,
  useIonViewWillLeave,
  IonTabButton,
  IonIcon,
  IonRouterOutlet,
  IonLabel,
  isPlatform,
  useIonRouter,
} from "@ionic/react";
import NotificationProvider from "@greeter-guest/contexts/NotificationProvider";
import { LoginPage } from "@greeter-guest/pages/LoginPage";
import { BookingSummaryRouteHandler } from "@greeter-guest/pages/BookingSummaryPage";
import PrivacyPage from "@greeter-guest/pages/PrivacyPage";
import { VenueOverviewPageRouteHandler } from "@greeter-guest/pages/VenueOverviewPage";
import { VenuePageApiHandler } from "@greeter-guest/pages/VenuePage/VenuePageApiHandler";
import { UserPageApiHandler } from "@greeter-guest/pages/UserPage/UserPageApiHandler";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { calendar, person, storefront } from "ionicons/icons";
import { IonReactRouter } from "@ionic/react-router";
import { Redirect, Route, useLocation } from "react-router-dom";
import {
  AuthProvider,
  GreeterGuestApiProvider,
  LoadingListener,
  useAuth,
  useGreeterGuestApi,
} from "@greeter/matter";
import { LoginProvider } from "./contexts/LoginProvider";
import { PushNotifications } from "@capacitor/push-notifications";
import { subscribeToPushNotifications } from "./utility/PushNotifications";
import { GreeterEventOverviewPageApiHandler } from "./pages/GreeterEventOverviewPage/GreeterEventOverviewPageApiHandler";
import { UpdateUserRouteHandler } from "./pages/UpdateUserPage/UpdateUserRouteHandler";
import { DeleteUserPage } from "./pages/DeleteUserPage/DeleteUserPage";
import { logger, warner } from "@greeter/log";
import { getConfig, getEnv, getFirebaseConfig } from "./utility/ConfigHooks";
import { partial } from "lodash";
import { MenuPageApiHandler } from "./pages/MenuPage/MenuPageApiHandler";
import { CartPageApiHandler } from "./pages/CartPage/CartPageApiHandler";
import { PaymentPage } from "./pages/PaymentPage/PaymentPage";
import { ReceiptPageApiHandler } from "./pages/ReceiptPage/ReceiptPageApiHandler";
import { Capacitor } from "@capacitor/core";
import { PostHogProvider, usePostHog } from "posthog-js/react";
import { CreateBookingPageV2RouteHandler } from "./pages/CreateBookingPageV2/CreateBookingPageV2RouteHandler";
import { createAuth } from "@greeter/auth";
import { GuestApi } from "@greeter/api";

import css from "./App.module.scss";
import { HelmetProvider } from "@dr.pogodin/react-helmet";
import { EventPageApiHandler } from "./pages/EventPage/EventPageApiHandler";

const queryClient = new QueryClient();
const NAME = "[App]";
const log = logger(NAME);
const warn = warner(NAME);

export function createGreeterGuestApi() {
  const config = getConfig();
  const env = getEnv();
  const firebaseConfig = getFirebaseConfig();
  const auth = createAuth({ env, firebaseConfig });
  return new GuestApi(config, auth);
}

const guestApi = createGreeterGuestApi();

function PageTracker() {
  const location = useLocation();
  const l = useMemo(() => partial(log, "[PageTracker]"), []);

  const [init, setInit] = useState(false);
  const posthog = usePostHog();

  useEffect(() => {
    try {
      if (!init) {
        //FirebaseAnalytics.setEnabled({ enabled: true });
        //hotjar.initialize(3303516, 6);
        setInit(true);
      }
    } catch {
      l("Failed to enable FirebaseAnalytics... Skipping...");
    }
  }, [init, l]);

  useEffect(() => {
    if (!init) return;

    const path = location.pathname + location.search;

    l("Analytics: Location changed", location);

    try {
      // FirebaseAnalytics.setCurrentScreen({
      //   screenName: location.pathname + location.search,
      // });
      // FirebaseAnalytics.logEvent({
      //   name: "screen_view",
      //   params: {
      //     screen_name: path,
      //     screen_class: document.title,
      //   },
      // });
      // hotjar.stateChange(path);
    } catch {
      l("Failed to trace page activity... Bailing...");
    }
  }, [init, location, l]);

  return null;
}

function LocationDebugger() {
  const location = useLocation();
  console.log("[LocationDebugger][location]", location);

  const router = useIonRouter();
  console.log("[LocationDebugger][router]", router.routeInfo);

  return null;
}

function HideTabBar({ children }: React.PropsWithChildren) {
  useIonViewWillEnter(() => {
    const tabBar = document.body.querySelector("ion-tab-bar");
    if (tabBar) {
      tabBar.style.display = "none";
    }
  });
  useIonViewWillLeave(() => {
    const tabBar = document.body.querySelector("ion-tab-bar");
    if (tabBar) {
      tabBar.style.removeProperty("display");
    }
  });
  return <>{children}</>;
}

const usePushNotifications = () => {
  const { isLoggedIn } = useAuth();
  const { api: guestApi } = useGreeterGuestApi();
  useEffect(() => {
    if (!Capacitor.isNativePlatform()) {
      warn("Not native platform. Skipping push notifications...");

      return;
    }

    if (isLoggedIn && guestApi) {
      (async () => {
        // We only subscribe if user has already "granted" push notifications,
        // so as to avoid a premature cancellation of the push notifications because the user
        // does not see why they should agree.
        // Granting permission should be after creating a booking.
        if (
          (await PushNotifications.checkPermissions()).receive === "granted"
        ) {
          await subscribeToPushNotifications({ isLoggedIn, guestApi });
        }
      })();
    }
  }, [guestApi, isLoggedIn]);
};

/**
 * Note for the future: CONDITIONAL ROUTES CAUSE ISSUES WITH THE HISTORY API!
 *
 * TODO: In the future we should select between split pane and tabs
 */
function Tabs() {
  usePushNotifications();

  const tabButtonLayout = isPlatform("desktop") ? "icon-start" : "icon-top";

  return (
    <IonTabs>
      <IonRouterOutlet>
        <Route exact path={Routes.home.template()}>
          <Redirect
            to={Routes.venuesTab.venues.route({}) + window.location.search}
          />
        </Route>
        <Route exact path={Routes.login.template()}>
          <LoginPage />
        </Route>
        <Route exact path={Routes.privacy.template()}>
          <PrivacyPage />
        </Route>
        {/* TODO: The original home page. */}
        {/*<Route exact path={Routes.home.template()}>
          <FrontPageApiHandler />
        </Route>*/}

        {/* TODO: Zwei Skive does not use the redirect link which they should! */}
        {/* This ensures that the old links a redirected to the new format. */}
        <Redirect
          from={Routes.venuesTab.deprecated_venue.template()}
          to={Routes.book.template()}
        />

        {/* NOTE: venues tab routes */}
        <Route
          exact
          path={Routes.venuesTab.venues.template()}
          component={VenueOverviewPageRouteHandler}
        />

        <Route
          exact
          path={Routes.venuesTab.venue.template()}
          component={VenuePageApiHandler}
        />

        <Redirect
          exact
          from={Routes.bookingSummary.template()}
          to={Routes.venuesTab.bookingSummary.template()}
        />

        <Route
          exact
          path={Routes.venuesTab.bookingSummary.template()}
          component={BookingSummaryRouteHandler}
        />

        {/* NOTE: This works since we aren't using the :tab param */}
        <Redirect
          exact
          from={Routes.greeterEvent.template()}
          to={Routes.greeterEventsTab.greeterEvent.template()}
        />

        <Route
          exact
          path={Routes.greeterEventsTab.greeterEvents.template()}
          component={GreeterEventOverviewPageApiHandler}
        />

        <Redirect
          exact
          from={Routes.greeterEventsTab.createBooking.template()}
          to={Routes.greeterEventsTab.book.template()}
        />
        <Route
          exact
          path={Routes.greeterEventsTab.book.template()}
          component={CreateBookingPageV2RouteHandler}
        />
        <Route
          exact
          path={Routes.greeterEventsTab.bookingSummary.template()}
          component={BookingSummaryRouteHandler}
        />
        <Route
          exact
          path={Routes.greeterEventsTab.greeterEvent.template()}
          component={GreeterEventPageRouteHandler}
        />

        {/* NOTE: profile tab routes */}
        <Route
          exact
          path={Routes.profileTab.profile.template()}
          component={UserPageApiHandler}
        />

        <Redirect
          exact
          push
          from={Routes.updateUser.template()}
          to={Routes.profileTab.updateUser.route()}
        />

        <Route
          exact
          path={Routes.profileTab.updateUser.template()}
          component={UpdateUserRouteHandler}
        />

        <Route exact path={Routes.deleteUser.template()}>
          <Redirect to={Routes.profileTab.deleteUser.route({})} />
        </Route>

        <Route
          exact
          path={Routes.profileTab.deleteUser.template()}
          component={DeleteUserPage}
        />

        <Route
          exact
          path={Routes.menu.template()}
          component={MenuPageApiHandler}
        />
        <Route
          exact
          path={Routes.cart.template()}
          component={CartPageApiHandler}
        />
        <Route exact path={Routes.payment.template()} component={PaymentPage} />

        {/* this supports the legacy /venue links. */}
        <Route
          exact
          path={Routes.createBooking.template()}
          render={({ match }) => {
            if (!match.params.venueId) {
              return <Redirect to={Routes.venues.route({})} />;
            }

            return (
              <Redirect
                to={
                  Routes.venuesTab.book.route({
                    venueId: match.params.venueId!,
                  }) + window.location.search
                } // Pass along the query params
              />
            );
          }}
        />

        <Redirect
          exact
          from={Routes.venuesTab.createBooking.template()}
          to={Routes.venuesTab.book.template()}
        />
        <Route
          exact
          path={Routes.venuesTab.book.template()}
          component={CreateBookingPageV2RouteHandler}
        />

        <Route
          exact
          path={Routes.receipt.template()}
          component={ReceiptPageApiHandler}
        />
      </IonRouterOutlet>
      <IonTabBar
        className={css.Navbar}
        slot={isPlatform("desktop") ? "top" : "bottom"}
      >
        {/* Hidden for now, until we have more volume for the front()page */}
        {/*<IonTabButton
          layout={tabButtonLayout}
          tab={Routes.home.route()}
          href={Routes.home.route()}
          className={css.NavbarItem}
          selected={location.pathname === Routes.home.route()}
        >
          <IonIcon icon={home} className={css.icon} />
          <IonLabel>Hjem</IonLabel>
        </IonTabButton>*/}
        <IonTabButton
          layout={tabButtonLayout}
          tab={Tab.Venues}
          href={Routes.venuesTab.venues.route({})}
          className={css.NavbarItem}
        >
          <IonIcon icon={storefront} className={css.icon} />
          <IonLabel>Venues</IonLabel>
        </IonTabButton>
        <IonTabButton
          layout={tabButtonLayout}
          tab={Tab.Events}
          href={Routes.greeterEventsTab.greeterEvents.route()}
          className={css.NavbarItem}
        >
          <IonIcon icon={calendar} className={css.icon} />
          <IonLabel>Events</IonLabel>
        </IonTabButton>
        <IonTabButton
          layout={tabButtonLayout}
          tab={Tab.Profile}
          href={Routes.profileTab.profile.route()}
          className={css.NavbarItem}
        >
          <IonIcon icon={person} className={css.icon} />
          <IonLabel>Profil</IonLabel>
        </IonTabButton>
      </IonTabBar>
    </IonTabs>
  );
}

export function App() {
  const config = useMemo(() => getConfig(), []);
  const env = useMemo(() => getEnv(), []);
  const firebaseConfig = useMemo(() => getFirebaseConfig(), []);

  return (
    <IonApp className={css.App}>
      <HelmetProvider>
        <PostHogProvider
          apiKey="phc_6OUeu1GfKjIxHDdpALF280hTbD5ixWEAB75rtPqk28B"
          options={{
            api_host: "https://eu.i.posthog.com",
            autocapture: true,
            person_profiles: "always", // or 'always' to create profiles for anonymous users as well
            persistence: "localStorage",
            upgrade: true,
            session_recording: {
              recordBody: true,
              collectFonts: true,
              maskAllInputs: true,
              inlineStylesheet: true,
            },
          }}
        >
          <GreeterGuestApiProvider api={guestApi}>
            <QueryClientProvider client={queryClient}>
              <NotificationProvider>
                <AuthProvider
                  env={env}
                  config={config}
                  firebaseConfig={firebaseConfig}
                >
                  <LoginProvider>
                    <IonReactRouter>
                      <IonRouterOutlet>
                        {/* NOTE: This was the old way. But there is some double rendering weirdness */}
                        {/*<Route component={Tabs} />*/}
                        <Tabs />

                        {/* NOTE: We've abstracted these 2 routes out,
                         * to simplify the booking flow in general without the need to first hide the navbar etc.
                         */}
                        <Route
                          exact
                          path={Routes.book.template()}
                          component={CreateBookingPageV2RouteHandler}
                        />
                        <Route
                          exact
                          path={Routes.bookingSummary.template()}
                          component={BookingSummaryRouteHandler}
                        />
                        <Route
                          exact
                          path={Routes.ticketEvent.template()}
                          component={EventPageApiHandler}
                        />
                      </IonRouterOutlet>
                      <PageTracker />
                      <LoadingListener />
                    </IonReactRouter>
                  </LoginProvider>
                </AuthProvider>
              </NotificationProvider>
            </QueryClientProvider>
          </GreeterGuestApiProvider>
        </PostHogProvider>
      </HelmetProvider>
    </IonApp>
  );
}
